import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { errorMessage, log } from '@/utils/logger';
import { showBridgedToast } from '@/contexts/toast-bridge';
import { APIError } from '@/utils/api-client';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      // useApiQuery always sets this explicitly (defaults to true); queries
      // that bypass the wrapper leave it undefined, so they stay silent here
      // exactly as before this handler existed.
      showErrorToast?: boolean;
      errorMessage?: string;
    };
  }
}

export const createQueryClient = () =>
  new QueryClient({
    // Global failure logging: every query/mutation error ships to BetterStack
    // (no-op when unconfigured) without per-handler wiring. Log only safe
    // fields - message + the key - never the raw error or response body.
    queryCache: new QueryCache({
      onError: (error, query) => {
        const status = error instanceof APIError ? error.status : undefined;

        const isNoDataNotFound = status === 404 || status === 401;
        log[isNoDataNotFound ? 'warn' : 'error']('Query failed', {
          queryKey: query.queryKey,
          error_message: errorMessage(error),
          ...(error instanceof APIError && {
            status: error.status,
            request_id: error.requestId,
          }),
        });

        if (!query.meta?.showErrorToast) return;

        // Components can render their own empty state off the error; opt a query
        // out of toasts entirely via meta.showErrorToast.
        if (isNoDataNotFound) return;

        showBridgedToast({
          title: 'Error',
          description:
            query.meta.errorMessage ||
            (error instanceof APIError
              ? error.message
              : 'An error occurred while loading data'),
          variant: 'destructive',
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        log.error('Mutation failed', {
          mutationKey: mutation.options.mutationKey,
          error_message: errorMessage(error),
          ...(error instanceof APIError && {
            status: error.status,
            request_id: error.requestId,
          }),
        }),
    }),
    defaultOptions: {
      queries: {
        // Stale time: how long until a query is considered stale
        staleTime: 1 * 60 * 1000, // 1 minute
        // Cache time: how long unused data stays in cache
        gcTime: 5 * 60 * 1000, // 5 minutes
        // Retry configuration
        retry: (failureCount: number, error: unknown) => {
          // Don't retry 4xx client errors (auth, validation, not-found): the
          // api-client throws APIError with a flat `status`, so match on that.
          // Retrying a 401 in particular would multiply the auth-refresh storm.
          if (
            error instanceof APIError &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false;
          }
          // Retry up to 3 times for other errors (network / 5xx)
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus
        refetchOnWindowFocus: true,
        // Refetch on reconnect
        refetchOnReconnect: true,
      },
      mutations: {
        // Writes aren't idempotent: a retry after a 5xx/timeout the server
        // already committed would duplicate it.
        retry: false,
      },
    },
  });
